import {
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { useList } from "@certa/catalyst/hooks/useList";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { classNames } from "@certa/catalyst/utils/common";
import type { ModelledAPIResponse } from "@certa/queries/types/globalSearch.types";
import React from "react";
import { useIntl } from "react-intl";
import {
  SEARCH_RESULT_TYPES,
  SEARCH_LISTBOX_ID,
  type SearchResultType
} from "../GlobalSearchWithSDO.constants";
import { useGlobalSearch } from "../hooks/useGlobalSearch";
import { useSearchStore } from "../store/useSearchStore";
import { mainContentStyles as styles } from "./AllResults.styles";
import { SearchResultsLoading, ResultItem } from "./ResultItem";
import { SkeletonGroup } from "@certa/catalyst/components/Skeleton";
import type { ListInteractionMode } from "@certa/catalyst/hooks/useListInteraction";
import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from "@certa/catalyst/components/Button";
import { useGetPreviewData } from "../hooks/useSearchKeyboard";
import { useGetSearchResultsLabelByType } from "../hooks/useGetSearchResultsLabelByType";
import { ChevronRight } from "@certa/icons/components/ChevronRight";
import { createSearchResultId } from "./AllResults";

type SearchResultsPreviewProps = {
  onResultClick: (
    result: ModelledAPIResponse.SearchResult,
    index: number,
    interactionMode: ListInteractionMode,
    query: string
  ) => void;
};

export const SearchResultsPreview: React.FC<SearchResultsPreviewProps> = ({
  onResultClick
}) => {
  const {
    selectedItem,
    setSelectedItem,
    interactionMode,
    setInteractionMode,
    setSelectedResultType,
    query
  } = useSearchStore();
  const { SEARCH_RESULT_TYPE_LABELS } = useGetSearchResultsLabelByType();

  const intl = useIntl();

  const searchResults = useGlobalSearch();

  const previewData = useGetPreviewData(searchResults);
  const allDataCount = previewData.allData.length;

  // Simple list hook instead of virtual list
  const { getListboxProps, getItemProps } = useList({
    activeIndex: selectedItem,
    selectedIndex: selectedItem,
    setActiveIndex: setSelectedItem,
    interactionMode,
    setInteractionMode,
    isCombobox: true,
    totalItemsCount: allDataCount,
    getOptionId: createSearchResultId,
    onSelect: (index: number, interactionMode: ListInteractionMode) => {
      const option = previewData.allData[index];
      onResultClick(option, index, interactionMode, query);
    }
  });

  const listBoxProps = getListboxProps({ label: "Search results" });

  const noResultsMessage = intl.formatMessage({
    id: "globalSearch.noResults",
    defaultMessage: "No results found."
  });

  const trySearchingMessage = intl.formatMessage({
    id: "globalSearch.trySearching",
    defaultMessage: "Try different keywords or filters."
  });

  const seeMoreLabel = intl.formatMessage({
    id: "globalSearch.seeMore",
    defaultMessage: "See more"
  });

  // Show loading if any of the enabled categories are loading
  if (searchResults.isAnyQueryLoading && !allDataCount) {
    return (
      <div
        className={classNames(styles.container, styles.loadingLayout)}
        data-testid="search-loading-skeleton"
      >
        <SkeletonGroup label="Loading search results">
          <SearchResultsLoading />
        </SkeletonGroup>
      </div>
    );
  }

  // Show empty state if no data is available and no loading
  if (!allDataCount && !searchResults.isAnyQueryLoading) {
    return (
      <Stack height="100%" justify="center" align="center" width="100%">
        <Typography
          variant={TypographyVariants.BODY}
          color={TypographyColors.NEUTRAL_700}
        >
          {noResultsMessage}
        </Typography>
        <Typography
          variant={TypographyVariants.BODY}
          color={TypographyColors.NEUTRAL_700}
        >
          {trySearchingMessage}
        </Typography>
      </Stack>
    );
  }

  const renderSection = (category: SearchResultType) => {
    const data = previewData[category];
    const shouldShowSeeMoreButton = previewData.showSeeMoreButton[category];

    if (!data.length) return null;
    return (
      <div key={category}>
        <Stack
          direction="horizontal"
          justify="space-between"
          dangerouslySetClassName={classNames(
            styles.rowHeading,
            styles.borderBottom
          )}
        >
          <Typography
            variant={TypographyVariants.LABEL_SM}
            color={TypographyColors.NEUTRAL_700}
          >
            {SEARCH_RESULT_TYPE_LABELS[category]}
          </Typography>
          {shouldShowSeeMoreButton ? (
            <Button
              onClick={() => setSelectedResultType(category)}
              rightIcon={<ChevronRight size={16} />}
              variant={ButtonVariants.TEXT}
              size={ButtonSizes.SMALL}
              color={ButtonColors.BRAND}
            >
              {seeMoreLabel}
            </Button>
          ) : null}
        </Stack>
        {data.map(option => {
          const globalIndex = previewData.allData.findIndex(
            data => data.id === option.id
          );
          const { optionProps } = getItemProps(globalIndex);
          const isSelected = globalIndex === selectedItem;

          return (
            <div key={option.id}>
              <div {...optionProps} className={styles.option}>
                <ResultItem item={option} isSelected={isSelected} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      {...listBoxProps}
      className={classNames(
        styles.container,
        listBoxProps.className,
        styles.paddingX,
        "scrollbar-on-hover"
      )}
      style={{
        overflow: "auto"
      }}
      id={SEARCH_LISTBOX_ID}
    >
      {renderSection(SEARCH_RESULT_TYPES.DATA_RECORDS)}
      {renderSection(SEARCH_RESULT_TYPES.WORKFLOWS)}
      {renderSection(SEARCH_RESULT_TYPES.FILES)}
    </div>
  );
};
